import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("wishlist");
    if (stored) {
      try {
        setWishlist(JSON.parse(stored));
      } catch (e) {
        console.error("Error parsing wishlist:", e);
        setWishlist([]);
      }
    }
  }, []);

  // Sync with backend when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      syncWishlistWithBackend();
    }
  }, [isAuthenticated, user?.id]);

  // Save to localStorage whenever wishlist changes
  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const syncWishlistWithBackend = async () => {
    if (!isAuthenticated || !user?.id) return;
    
    try {
      setLoading(true);
      // Get user's wishlist from backend
      const response = await axios.get(`${API}/wishlist/${user.id}`);
      const backendWishlist = response.data.items || [];
      
      // Merge local wishlist with backend wishlist
      const localWishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
      const mergedIds = [...new Set([...localWishlist.map(p => p.id), ...backendWishlist.map(p => p.id)])];
      
      // Update backend with merged list
      if (localWishlist.length > 0) {
        await axios.post(`${API}/wishlist/${user.id}/sync`, { product_ids: mergedIds });
      }
      
      // Fetch full product data for merged wishlist
      const fullResponse = await axios.get(`${API}/wishlist/${user.id}`);
      setWishlist(fullResponse.data.items || []);
    } catch (error) {
      console.error("Error syncing wishlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = useCallback(async (product) => {
    // Check if already in wishlist
    if (wishlist.some(item => item.id === product.id)) {
      return { success: false, message: "Already in wishlist" };
    }

    const newWishlist = [...wishlist, product];
    setWishlist(newWishlist);

    // Sync with backend if authenticated
    if (isAuthenticated && user?.id) {
      try {
        await axios.post(`${API}/wishlist/${user.id}/add`, { product_id: product.id });
      } catch (error) {
        console.error("Error adding to wishlist on backend:", error);
      }
    }

    return { success: true, message: "Added to wishlist" };
  }, [wishlist, isAuthenticated, user?.id]);

  const removeFromWishlist = useCallback(async (productId) => {
    const newWishlist = wishlist.filter(item => item.id !== productId);
    setWishlist(newWishlist);

    // Sync with backend if authenticated
    if (isAuthenticated && user?.id) {
      try {
        await axios.delete(`${API}/wishlist/${user.id}/remove/${productId}`);
      } catch (error) {
        console.error("Error removing from wishlist on backend:", error);
      }
    }

    return { success: true, message: "Removed from wishlist" };
  }, [wishlist, isAuthenticated, user?.id]);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => item.id === productId);
  }, [wishlist]);

  const clearWishlist = useCallback(async () => {
    setWishlist([]);
    localStorage.removeItem("wishlist");

    if (isAuthenticated && user?.id) {
      try {
        await axios.delete(`${API}/wishlist/${user.id}/clear`);
      } catch (error) {
        console.error("Error clearing wishlist on backend:", error);
      }
    }
  }, [isAuthenticated, user?.id]);

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        loading,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        wishlistCount: wishlist.length
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
